import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Contact, PressListContact, PressAsset } from '@pf/shared';
import {
  usePressList,
  useAddContactToList,
  useRemoveContactFromList,
  useReorderContactsInList,
  useDuplicatePressList,
  useArchivePressList,
  useUnarchivePressList,
  useUpdateContactSelectedEmail,
  usePressListComments,
  useAddPressListComment,
  useDeletePressListComment,
  usePressListSuggestions,
  usePressListAssets,
  useUploadPressAsset,
  useDeletePressAsset,
  ReporterSuggestion,
  CommentWithUser,
} from '../hooks/usePressLists';
import { useBatchImportReporters } from '../hooks/useContacts';
import { useContacts } from '../hooks/useContacts';
import { apiDownload } from '../lib/api';
import { useMessages } from '../hooks/useSendBatches';
import ThreadViewModal from '../components/ThreadViewModal';

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── Add Contact Modal ────────────────────────────────────────────────────────

interface AddContactModalProps {
  listId: string;
  existingContactIds: Set<string>;
  onClose: () => void;
}

function AddContactModal({
  listId,
  existingContactIds,
  onClose,
}: AddContactModalProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const addContact = useAddContactToList();
  const [adding, setAdding] = useState<string | null>(null);
  const [error, setError] = useState('');

  const { data, isLoading } = useContacts({
    page: 1,
    limit: 20,
    search: debouncedSearch || undefined,
  });

  const contacts = data?.data ?? [];
  const available = contacts.filter((c) => !existingContactIds.has(c.id));

  async function handleAdd(contact: Contact) {
    setAdding(contact.id);
    setError('');
    try {
      await addContact.mutateAsync({ listId, body: { contactId: contact.id } });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add contact');
      setAdding(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 flex flex-col max-h-[80vh]">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Add Contact</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="px-6 py-3 border-b border-gray-200 flex-shrink-0">
          <input
            autoFocus
            type="search"
            placeholder="Search contacts by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        {error && (
          <div className="px-6 py-2 flex-shrink-0">
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}
        <div className="overflow-y-auto flex-1">
          {isLoading && (
            <div className="px-6 py-6 text-center text-sm text-gray-500">
              Loading...
            </div>
          )}
          {!isLoading && available.length === 0 && (
            <div className="px-6 py-6 text-center text-sm text-gray-500">
              {search
                ? 'No contacts match your search.'
                : 'No contacts available to add.'}
            </div>
          )}
          {!isLoading && available.length > 0 && (
            <ul className="divide-y divide-gray-100">
              {available.map((c) => (
                <li
                  key={c.id}
                  className="px-6 py-3 flex items-center justify-between hover:bg-gray-50"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {c.firstName} {c.lastName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {[c.email, c.company].filter(Boolean).join(' · ') ||
                        'No additional info'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleAdd(c)}
                    disabled={adding === c.id}
                    className="text-xs bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 px-3 py-1 rounded-md font-medium"
                  >
                    {adding === c.id ? 'Adding...' : 'Add'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="px-6 py-3 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit Email Popover ───────────────────────────────────────────────────────

interface EditEmailProps {
  listId: string;
  contactId: string;
  currentEmail: string | null;
  contactEmail: string | null;
  onClose: () => void;
}

function EditEmailPopover({
  listId,
  contactId,
  currentEmail,
  contactEmail,
  onClose,
}: EditEmailProps) {
  const updateEmail = useUpdateContactSelectedEmail();
  const [value, setValue] = useState(currentEmail ?? contactEmail ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  async function handleSave() {
    await updateEmail.mutateAsync({
      listId,
      contactId,
      selectedEmail: value.trim() || null,
    });
    onClose();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') onClose();
  }

  return (
    <div className="absolute right-0 top-8 z-20 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-64">
      <p className="text-xs font-medium text-gray-600 mb-2">Override email</p>
      <input
        ref={inputRef}
        type="email"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={contactEmail ?? 'Enter email...'}
        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      {currentEmail && (
        <button
          onClick={() => {
            setValue('');
            updateEmail.mutateAsync({ listId, contactId, selectedEmail: null });
            onClose();
          }}
          className="mt-1 text-xs text-gray-500 hover:text-gray-700"
        >
          Clear override (use contact email)
        </button>
      )}
      <div className="flex gap-2 mt-2">
        <button
          onClick={onClose}
          className="flex-1 px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={updateEmail.isPending}
          className="flex-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          Save
        </button>
      </div>
    </div>
  );
}

// ─── Sortable Row ─────────────────────────────────────────────────────────────

interface SortableRowProps {
  plc: PressListContact & { contact?: Contact | null };
  listId: string;
  onRemove: (contactId: string, locked: boolean) => void;
  removingId: string | null;
}

function SortableRow({ plc, listId, onRemove, removingId }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: plc.contactId });

  const navigate = useNavigate();
  const [showEmailEdit, setShowEmailEdit] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const c = plc.contact;
  const reporter = c?.reporter as
    | (Contact['reporter'] & { beats?: string[]; primaryPublicationId?: string })
    | null
    | undefined;
  const displayEmail = plc.selectedEmail ?? c?.email ?? '—';
  const beats: string[] = reporter?.beats ?? [];

  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-gray-50 transition-colors">
      {/* Drag handle */}
      <td className="px-2 py-3 w-8">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 touch-none"
          title="Drag to reorder"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
          </svg>
        </button>
      </td>

      {/* Name */}
      <td className="px-4 py-3 text-sm font-medium text-gray-900">
        <div className="flex items-center gap-2">
          {plc.locked && (
            <span title="Locked — message has been sent">
              <svg
                className="w-3.5 h-3.5 text-amber-500 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          )}
          {c ? (
            <button
              onClick={() => navigate(`/contacts/${c.id}`)}
              className="hover:underline text-left"
            >
              {c.firstName} {c.lastName}
            </button>
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </div>
      </td>

      {/* Email */}
      <td className="px-4 py-3 text-sm text-gray-500">
        <div className="flex items-center gap-1 relative">
          <span className={plc.selectedEmail ? 'text-blue-600 font-medium' : ''}>
            {displayEmail}
          </span>
          <div className="relative">
            <button
              onClick={() => setShowEmailEdit((v) => !v)}
              className="text-gray-400 hover:text-gray-600 ml-1"
              title="Edit override email"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            {showEmailEdit && c && (
              <EditEmailPopover
                listId={listId}
                contactId={c.id}
                currentEmail={plc.selectedEmail}
                contactEmail={c.email}
                onClose={() => setShowEmailEdit(false)}
              />
            )}
          </div>
        </div>
      </td>

      {/* Company */}
      <td className="px-4 py-3 text-sm text-gray-500">{c?.company ?? '—'}</td>

      {/* Publication (from reporter) */}
      <td className="px-4 py-3 text-sm text-gray-500">
        {(reporter as { name?: string } | null | undefined)?.name ?? '—'}
      </td>

      {/* Beats */}
      <td className="px-4 py-3">
        {beats.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {beats.slice(0, 3).map((beat) => (
              <span
                key={beat}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700"
              >
                {beat}
              </span>
            ))}
            {beats.length > 3 && (
              <span className="text-xs text-gray-400">+{beats.length - 3}</span>
            )}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">—</span>
        )}
      </td>

      {/* Notes */}
      <td className="px-4 py-3 text-sm text-gray-500 max-w-[120px] truncate">
        {plc.notes ?? '—'}
      </td>

      {/* Actions */}
      <td className="px-4 py-3 text-right">
        {plc.locked ? (
          <span
            title="Locked — cannot be removed after message sent"
            className="inline-flex items-center gap-1 text-xs text-amber-600"
          >
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clipRule="evenodd"
              />
            </svg>
            Locked
          </span>
        ) : (
          <button
            onClick={() => onRemove(plc.contactId, plc.locked)}
            disabled={removingId === plc.contactId}
            className="text-xs text-red-600 hover:text-red-800 disabled:opacity-30 font-medium"
          >
            {removingId === plc.contactId ? 'Removing...' : 'Remove'}
          </button>
        )}
      </td>
    </tr>
  );
}

// ─── Send History Section ─────────────────────────────────────────────────────

function SendHistoryStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-600',
    queued: 'bg-yellow-100 text-yellow-800',
    sending: 'bg-blue-100 text-blue-800',
    sent: 'bg-green-100 text-green-800',
    delivered: 'bg-green-100 text-green-800',
    opened: 'bg-purple-100 text-purple-800',
    replied: 'bg-indigo-100 text-indigo-800',
    bounced: 'bg-red-100 text-red-800',
    failed: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

function SendHistorySection({ listId }: { listId: string }) {
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  const { data, isLoading } = useMessages({
    pressListId: listId,
    status: statusFilter || undefined,
    page,
    limit: 20,
  });

  const msgs = data?.messages ?? [];
  const pagination = data?.pagination;

  const sentCount = msgs.filter((m) => ['sent', 'delivered', 'opened', 'replied'].includes(m.status)).length;
  const repliedCount = msgs.filter((m) => m.status === 'replied').length;
  const openedCount = msgs.filter((m) => ['opened', 'replied'].includes(m.status)).length;
  const bouncedCount = msgs.filter((m) => m.status === 'bounced').length;

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">Send History</h2>
        {pagination && pagination.total > 0 && (
          <span className="text-xs text-gray-400">{pagination.total} messages</span>
        )}
      </div>

      {/* Quick stats */}
      {!isLoading && msgs.length > 0 && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Sent', value: sentCount, color: 'text-green-600' },
            { label: 'Opened', value: openedCount, color: 'text-purple-600' },
            { label: 'Replied', value: repliedCount, color: 'text-indigo-600' },
            { label: 'Bounced', value: bouncedCount, color: 'text-red-600' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-gray-50 rounded-md p-2.5 text-center">
              <div className={`text-lg font-semibold ${color}`}>{value}</div>
              <div className="text-xs text-gray-500">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Status filter */}
      <div className="flex items-center gap-2">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="text-xs border border-gray-300 rounded-md px-2 py-1 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="opened">Opened</option>
          <option value="replied">Replied</option>
          <option value="bounced">Bounced</option>
          <option value="failed">Failed</option>
          <option value="queued">Queued</option>
        </select>
      </div>

      {isLoading && <p className="text-sm text-gray-400">Loading send history...</p>}
      {!isLoading && msgs.length === 0 && (
        <p className="text-sm text-gray-400">No messages sent from this press list yet.</p>
      )}
      {!isLoading && msgs.length > 0 && (
        <>
          <div className="divide-y divide-gray-100">
            {msgs.map((msg) => (
              <div
                key={msg.id}
                onClick={() => setSelectedMessageId(msg.id)}
                className="flex items-center justify-between py-2.5 cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {msg.contactFirstName || msg.contactLastName
                      ? `${msg.contactFirstName ?? ''} ${msg.contactLastName ?? ''}`.trim()
                      : msg.toEmail}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {msg.subject ?? '(no subject)'}
                    {msg.sentAt && (
                      <span className="ml-2">
                        · {new Date(msg.sentAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </span>
                    )}
                    {msg.openCount > 0 && (
                      <span className="ml-2 text-purple-600">{msg.openCount} open{msg.openCount !== 1 ? 's' : ''}</span>
                    )}
                  </p>
                </div>
                <div className="ml-3 shrink-0">
                  <SendHistoryStatusBadge status={msg.status} />
                </div>
              </div>
            ))}
          </div>

          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between pt-2 text-xs text-gray-500">
              <span>Page {pagination.page} of {pagination.pages}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-2 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  className="px-2 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {selectedMessageId && (
        <ThreadViewModal messageId={selectedMessageId} onClose={() => setSelectedMessageId(null)} />
      )}
    </div>
  );
}

// ─── Comments Section ─────────────────────────────────────────────────────────

interface CommentsSectionProps {
  listId: string;
  currentUserId?: string;
}

function CommentsSection({ listId, currentUserId }: CommentsSectionProps) {
  const { data, isLoading } = usePressListComments(listId);
  const addComment = useAddPressListComment();
  const deleteComment = useDeletePressListComment();
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const commentsList: CommentWithUser[] = data?.comments ?? [];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await addComment.mutateAsync({ listId, text: text.trim() });
      setText('');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(commentId: string) {
    setDeletingId(commentId);
    try {
      await deleteComment.mutateAsync({ listId, commentId });
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 mb-3">Comments</h3>
      {isLoading && (
        <p className="text-sm text-gray-400">Loading comments...</p>
      )}
      {!isLoading && commentsList.length === 0 && (
        <p className="text-sm text-gray-400">No comments yet.</p>
      )}
      {!isLoading && commentsList.length > 0 && (
        <ul className="space-y-3 mb-4">
          {commentsList.map((c) => (
            <li key={c.id} className="group">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs font-medium text-gray-800">
                      {c.userName}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(c.createdAt).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mt-0.5 break-words">
                    {c.text}
                  </p>
                </div>
                {c.userId === currentUserId && (
                  <button
                    onClick={() => handleDelete(c.id)}
                    disabled={deletingId === c.id}
                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity flex-shrink-0"
                    title="Delete comment"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a comment..."
          className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="submit"
          disabled={submitting || !text.trim()}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          Post
        </button>
      </form>
    </div>
  );
}

// ─── Suggested Reporters Section ─────────────────────────────────────────────

function SuggestedReportersSection({ listId }: { listId: string }) {
  const { data, isLoading } = usePressListSuggestions(listId);
  const batchImport = useBatchImportReporters();
  const [addingId, setAddingId] = useState<string | null>(null);
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());

  async function handleAdd(reporter: ReporterSuggestion) {
    setAddingId(reporter.id);
    try {
      await batchImport.mutateAsync({ reporterIds: [reporter.id], pressListId: listId });
      setAddedIds((prev) => new Set([...prev, reporter.id]));
    } finally {
      setAddingId(null);
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow mb-6 p-6">
        <p className="text-sm text-gray-500">Loading suggestions...</p>
      </div>
    );
  }

  if (data?.message) {
    return (
      <div className="bg-white rounded-lg shadow mb-6 p-6">
        <h2 className="text-sm font-semibold text-gray-900 mb-2">Suggested Reporters</h2>
        <p className="text-sm text-gray-500">{data.message}</p>
      </div>
    );
  }

  const suggestions = data?.suggestions ?? [];
  if (suggestions.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Suggested Reporters</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Based on the reporters already in your list
          </p>
        </div>
      </div>
      <div className="divide-y divide-gray-100">
        {suggestions.map((r) => {
          const isAdded = addedIds.has(r.id);
          return (
            <div key={r.id} className="px-4 py-3 flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 truncate">{r.name}</span>
                  {r.title && (
                    <span className="text-xs text-gray-500 truncate hidden sm:block">
                      {r.title}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {r.primaryPublication?.name && (
                    <span className="text-xs text-gray-600">
                      {r.primaryPublication.name}
                    </span>
                  )}
                  {r.beats.slice(0, 3).map((beat) => (
                    <span
                      key={beat}
                      className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-700"
                    >
                      {beat}
                    </span>
                  ))}
                  <span className="text-xs text-gray-400 ml-auto">
                    {Math.round(r.relevanceScore * 100)}% match
                  </span>
                </div>
              </div>
              <button
                onClick={() => handleAdd(r)}
                disabled={addingId === r.id || isAdded}
                className="flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-md border disabled:opacity-50
                  bg-white border-gray-300 text-gray-700 hover:bg-gray-50
                  disabled:cursor-default"
              >
                {isAdded ? 'Added' : addingId === r.id ? 'Adding...' : 'Add to List'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Assets Section ───────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileIcon({ fileType }: { fileType: string }) {
  if (fileType.startsWith('image/')) {
    return (
      <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    );
  }
  if (fileType === 'application/pdf') {
    return (
      <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  }
  return (
    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function AssetCard({
  asset,
  onDelete,
}: {
  asset: PressAsset;
  onDelete: (id: string) => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isImage = asset.fileType.startsWith('image/');

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white hover:shadow-sm transition-shadow">
      {/* Preview area */}
      <div className="h-28 bg-gray-50 flex items-center justify-center overflow-hidden">
        {isImage ? (
          <img
            src={asset.fileUrl}
            alt={asset.filename}
            className="h-full w-full object-cover"
          />
        ) : (
          <FileIcon fileType={asset.fileType} />
        )}
      </div>

      {/* Info */}
      <div className="p-3">
        <p
          className="text-xs font-medium text-gray-900 truncate"
          title={asset.filename}
        >
          {asset.filename}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">{formatBytes(asset.fileSize)}</p>
        {asset.notes && (
          <p className="text-xs text-gray-500 mt-1 truncate" title={asset.notes}>
            {asset.notes}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="px-3 pb-3 flex items-center gap-2">
        <a
          href={asset.fileUrl}
          download={asset.filename}
          className="text-xs text-blue-600 hover:text-blue-800"
        >
          Download
        </a>
        <span className="text-gray-200">|</span>
        {confirmDelete ? (
          <span className="flex items-center gap-1">
            <button
              onClick={() => onDelete(asset.id)}
              className="text-xs text-red-600 hover:text-red-800 font-medium"
            >
              Confirm
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Cancel
            </button>
          </span>
        ) : (
          <button
            onClick={() => setConfirmDelete(true)}
            className="text-xs text-red-500 hover:text-red-700"
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

function AssetsSection({ listId }: { listId: string }) {
  const { data, isLoading } = usePressListAssets(listId);
  const upload = useUploadPressAsset();
  const deleteAsset = useDeletePressAsset();
  const [dragging, setDragging] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const assets = data?.assets ?? [];

  async function handleFiles(files: File[]) {
    setUploadError('');
    for (const file of files) {
      try {
        await upload.mutateAsync({ listId, file });
      } catch {
        setUploadError(`Failed to upload ${file.name}`);
      }
    }
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) handleFiles(files);
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) handleFiles(files);
    e.target.value = '';
  }

  async function handleDelete(assetId: string) {
    try {
      await deleteAsset.mutateAsync({ listId, assetId });
    } catch {
      setUploadError('Failed to delete file');
    }
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-sm font-semibold text-gray-900 mb-4">
        Assets
        {assets.length > 0 && (
          <span className="ml-1 font-normal text-gray-500">({assets.length})</span>
        )}
      </h2>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors mb-4 ${
          dragging
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileInput}
          accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
        />
        <svg
          className={`mx-auto w-8 h-8 mb-2 ${dragging ? 'text-blue-400' : 'text-gray-400'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
        </svg>
        <p className="text-sm text-gray-600">
          {upload.isPending ? 'Uploading...' : 'Drag & drop files or click to browse'}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Images, PDFs, documents — up to 50 MB each
        </p>
      </div>

      {uploadError && (
        <p className="text-sm text-red-600 mb-3">{uploadError}</p>
      )}

      {isLoading && (
        <p className="text-sm text-gray-400">Loading assets...</p>
      )}

      {!isLoading && assets.length === 0 && (
        <p className="text-sm text-gray-400">No assets yet. Upload files to build your media kit.</p>
      )}

      {assets.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {assets.map((asset) => (
            <AssetCard key={asset.id} asset={asset} onDelete={handleDelete} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function PressListDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = usePressList(id);
  const removeContact = useRemoveContactFromList();
  const reorder = useReorderContactsInList();
  const duplicate = useDuplicatePressList();
  const archive = useArchivePressList();
  const unarchive = useUnarchivePressList();

  const [showAdd, setShowAdd] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [localContacts, setLocalContacts] = useState<
    (PressListContact & { contact?: Contact | null })[]
  >([]);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);

  const currentUser = JSON.parse(localStorage.getItem('pf_user') ?? '{}') as {
    id?: string;
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const list = data?.pressList;
  const serverContacts = data?.contacts ?? [];

  // Sync local order when server data arrives
  useEffect(() => {
    if (serverContacts.length > 0 || (data && localContacts.length === 0)) {
      setLocalContacts(serverContacts as (PressListContact & { contact?: Contact | null })[]);
    }
  }, [data]);

  // Close actions menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (actionsRef.current && !actionsRef.current.contains(e.target as Node)) {
        setShowActionsMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const existingContactIds = new Set(localContacts.map((plc) => plc.contactId));

  async function handleRemove(contactId: string, locked: boolean) {
    if (locked || !id) return;
    setRemovingId(contactId);
    try {
      await removeContact.mutateAsync({ listId: id, contactId });
      setLocalContacts((prev) => prev.filter((plc) => plc.contactId !== contactId));
    } finally {
      setRemovingId(null);
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id || !id) return;

    setLocalContacts((items) => {
      const oldIndex = items.findIndex((i) => i.contactId === active.id);
      const newIndex = items.findIndex((i) => i.contactId === over.id);
      const reordered = arrayMove(items, oldIndex, newIndex);
      // Persist asynchronously
      reorder.mutate({
        listId: id,
        contactIds: reordered.map((i) => i.contactId),
      });
      return reordered;
    });
  }

  async function handleDuplicate() {
    if (!id) return;
    try {
      const result = await duplicate.mutateAsync(id);
      navigate(`/press-lists/${result.pressList.id}`);
    } catch {
      // error handled by mutation
    }
  }

  async function handleExport(format: 'csv' | 'xlsx') {
    if (!id || !list) return;
    setExporting(true);
    setShowActionsMenu(false);
    try {
      const safeListName = list.name.replace(/[^a-z0-9_-]/gi, '_').slice(0, 50);
      await apiDownload(
        `/press-lists/${id}/export`,
        { format },
        `${safeListName}.${format}`,
      );
    } finally {
      setExporting(false);
    }
  }

  async function handleArchiveToggle() {
    if (!id || !list) return;
    try {
      if (list.archivedAt) {
        await unarchive.mutateAsync(id);
      } else {
        await archive.mutateAsync(id);
        navigate('/press-lists');
      }
    } catch {
      // error handled by mutation
    }
    setShowActionsMenu(false);
  }

  if (isLoading) {
    return <div className="p-6 text-sm text-gray-500">Loading...</div>;
  }

  if (isError || !list) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-600">Failed to load press list.</p>
        <button
          onClick={() => navigate('/press-lists')}
          className="mt-2 text-sm text-blue-600 hover:underline"
        >
          Back to Press Lists
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl">
      {showAdd && id && (
        <AddContactModal
          listId={id}
          existingContactIds={existingContactIds}
          onClose={() => setShowAdd(false)}
        />
      )}

      {/* Breadcrumb */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/press-lists')}
          className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Press Lists
        </button>
        <span className="text-gray-300">/</span>
        <span className="text-sm text-gray-900 font-medium">{list.name}</span>
        {list.archivedAt && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
            Archived
          </span>
        )}
      </div>

      {/* Header card */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-gray-900">{list.name}</h1>
            {list.description && (
              <p className="mt-1 text-sm text-gray-500">{list.description}</p>
            )}
            {list.tags && list.tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {list.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to={`/compose?pressListId=${id}`}
              className="inline-flex items-center gap-1.5 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-md text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Compose
            </Link>
            <button
              onClick={() => setShowAdd(true)}
              className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-md text-sm font-medium"
            >
              Add Contact
            </button>

            {/* Actions dropdown */}
            <div ref={actionsRef} className="relative">
              <button
                onClick={() => setShowActionsMenu((v) => !v)}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </button>
              {showActionsMenu && (
                <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  <button
                    onClick={handleDuplicate}
                    disabled={duplicate.isPending}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 first:rounded-t-lg"
                  >
                    Duplicate list
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    disabled={exporting}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {exporting ? 'Exporting...' : 'Export as CSV'}
                  </button>
                  <button
                    onClick={() => handleExport('xlsx')}
                    disabled={exporting}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Export as XLSX
                  </button>
                  <button
                    onClick={handleArchiveToggle}
                    disabled={archive.isPending || unarchive.isPending}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 last:rounded-b-lg"
                  >
                    {list.archivedAt ? 'Unarchive list' : 'Archive list'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contacts table */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">
            Contacts{' '}
            <span className="font-normal text-gray-500">
              ({localContacts.length})
            </span>
          </h2>
        </div>

        {localContacts.length === 0 && (
          <div className="px-6 py-10 text-center text-sm text-gray-500">
            No contacts yet. Add your first one.
          </div>
        )}

        {localContacts.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-8 px-2 py-3" />
                  {[
                    'Name',
                    'Email',
                    'Company',
                    'Publication',
                    'Beats',
                    'Notes',
                    '',
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={localContacts.map((plc) => plc.contactId)}
                  strategy={verticalListSortingStrategy}
                >
                  <tbody className="bg-white divide-y divide-gray-100">
                    {localContacts.map((plc) => (
                      <SortableRow
                        key={plc.id}
                        plc={plc}
                        listId={id!}
                        onRemove={handleRemove}
                        removingId={removingId}
                      />
                    ))}
                  </tbody>
                </SortableContext>
              </DndContext>
            </table>
          </div>
        )}
      </div>

      {/* Suggested reporters */}
      <SuggestedReportersSection listId={id!} />

      {/* Send history */}
      <SendHistorySection listId={id!} />

      {/* Comments section */}
      <div className="bg-white rounded-lg shadow p-6">
        <CommentsSection listId={id!} currentUserId={currentUser.id} />
      </div>

      {/* Assets section */}
      <AssetsSection listId={id!} />
    </div>
  );
}
